/* eslint-disable no-console */
/**
 * RP-668: TRTO-based validation for package pricing overrides.
 *
 * Pure helper functions used by the Rental item-search pricing flow to build the
 * legal/price request payload and to derive the Maximum Allowed Term from the
 * legal/price response.
 */

import { LegalPriceRequest } from '../types/types';

/**
 * Builds the legal/price request body from the current agreement's store number
 * and the inventory items currently added to the agreement.
 */
export const buildLegalPriceRequest = (
  storeNumber: string | null,
  inventoriesValue: { inventoryPriceId: number | string }[]
): LegalPriceRequest => {
  const inventoryPriceIds: string[] = (inventoriesValue || [])
    .map((item) => item?.inventoryPriceId)
    .filter((id) => id !== undefined && id !== null)
    .map((id) => String(id));

  return {
    storeNumber: storeNumber || '',
    inventoryPriceId: inventoryPriceIds,
  };
};

/**
 * Calculates the Maximum Allowed Term from the legal total cost and current agreement rate.
 * Returns whichever is higher between the legal-derived term and the item-price term,
 * so the user is never restricted below what item-price already allows.
 *
 * This function assumes inputs are already validated (non-zero rate, numeric totalCost).
 * Caller is responsible for validation and fallback handling.
 */
export const deriveMaximumAllowedTerm = (
  totalCost: string,
  currentAgreementRate: string | number,
  itemPriceMaxTerm: number
): number => {
  const legalMaxTerm = Math.floor(
    Number(totalCost) / Number(currentAgreementRate)
  );
  return legalMaxTerm && legalMaxTerm >= itemPriceMaxTerm
    ? legalMaxTerm
    : itemPriceMaxTerm;
};
